/**
 * Book Details' community-data hook — wraps data/communityBookData.js's
 * deterministic mock generators and layers the viewer's own submitted
 * review on top, persisted locally (mirrors CoverClubContext's `myComments`
 * pattern: only the user's own interaction persists, the base data is
 * seeded/generated). Scoped to this page tree only — nothing outside Book
 * Details reads a user's reviews today, so this stays a hook, not a new
 * global context; promote it if that changes.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/lib/useAuth';
import {
  getRatingSummary,
  getPopularTags,
  getCurrentlyReadingSocialProof,
  getRecentActivity,
} from '../data/communityBookData';

const REVIEW_PAGE_SIZE = 5;

export function useBookCommunityData(book) {
  const bookId = book?.id ?? null;
  const bookTitle = book?.title ?? null;
  const { user, getBookReviews, createBookReview, updateBookReview, deleteBookReview } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState('');
  const [visibleCount, setVisibleCount] = useState(REVIEW_PAGE_SIZE);

  const ratingSummary = useMemo(
    () => (bookId ? getRatingSummary(bookId, book?.rating) : null),
    [bookId, book?.rating]
  );

  const tags = useMemo(
    () => (bookId ? getPopularTags(bookId, book?.subjects) : []),
    [bookId, book?.subjects]
  );

  const currentlyReading = useMemo(
    () => (bookId ? getCurrentlyReadingSocialProof(bookId, book?.friendsReading) : { count: 0, sample: [] }),
    [bookId, book?.friendsReading]
  );

  const recentActivity = useMemo(
    () => (bookId ? getRecentActivity(bookId, bookTitle) : []),
    [bookId, bookTitle]
  );

  const normalizeReview = useCallback((review) => {
    const author = review.user || review.author || {};
    const name = review.name || review.displayName || author.displayName || author.name || author.username || 'Reader';
    return {
      ...review,
      id: review.id || review.reviewId,
      name,
      handle: review.handle || author.username,
      tone: review.tone || author.avatarTone || 'coral',
      rating: review.rating ?? null,
      quote: review.quote || '',
      body: review.body || '',
      likes: review.likes ?? review.likeCount ?? 0,
      createdAt: review.createdAt ? new Date(review.createdAt).getTime() : (review.createdAt || Date.now()),
      mine: review.mine || review.isOwner || (user?.id && author.id === user.id),
    };
  }, [user]);

  useEffect(() => {
    if (!bookId) return undefined;
    let active = true;
    setReviewsLoading(true);
    setReviewsError('');
    setVisibleCount(REVIEW_PAGE_SIZE);

    getBookReviews(bookId)
      .then((payload) => {
        if (!active) return;
        const result = payload?.data ?? payload;
        const rawReviews = result?.reviews || result?.items || result?.results || (Array.isArray(result) ? result : []);
        setReviews(rawReviews.map(normalizeReview).filter((review) => review.id));
      })
      .catch((error) => {
        if (active) setReviewsError(error.message || 'We could not load the reader reviews.');
      })
      .finally(() => {
        if (active) setReviewsLoading(false);
      });

    return () => { active = false; };
  }, [bookId, getBookReviews, normalizeReview]);

  const myReview = reviews.find((review) => review.mine) ?? null;
  const visibleReviews = reviews.slice(0, visibleCount);
  const hasMoreReviews = visibleCount < reviews.length;

  const loadMoreReviews = useCallback(() => setVisibleCount((n) => n + REVIEW_PAGE_SIZE), []);

  const submitReview = useCallback(async (review) => {
    if (!bookId) return;
    const payload = myReview
      ? await updateBookReview(myReview.id, review)
      : await createBookReview(bookId, review);
    const result = payload?.data?.review || payload?.data || payload?.review || payload;
    const nextReview = normalizeReview({ ...review, ...result, mine: true });
    setReviews((previous) => myReview
      ? previous.map((item) => (item.id === myReview.id ? nextReview : item))
      : [nextReview, ...previous]);
  }, [bookId, createBookReview, myReview, normalizeReview, updateBookReview]);

  const removeReview = useCallback(async (reviewId) => {
    await deleteBookReview(reviewId);
    setReviews((previous) => previous.filter((review) => review.id !== reviewId));
  }, [deleteBookReview]);

  return {
    ratingSummary,
    tags,
    currentlyReading,
    recentActivity,
    reviews: visibleReviews,
    totalReviews: reviews.length,
    hasMoreReviews,
    reviewsLoading,
    reviewsError,
    loadMoreReviews,
    myReview,
    submitReview,
    removeReview,
  };
}
